import React, { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { apiBaseUrl } from '../../util/api';

import {
  IconCheckmark,
  IconCopy,
  IconEdit,
  IconDelete,
  InlineTextButton,
  Modal,
  Button,
} from '../index';

import css from './AvailabilityIntegration.module.css';

const EXTERNAL_SERVICE_TITLE_MAX_LENGTH = 64;

const AvailabilityIntegration = props => {
  const intl = useIntl();
  const {
    listingId,
    externalServices,
    onManageDisableScrolling,
    onAddExternalService,
    onUpdateExternalService,
    onRemoveExternalService,
  } = props;

  const [isCopied, setIsCopied] = useState(false);
  const [isExternalServiceModalOpen, setIsExternalServiceModalOpen] = useState(false);
  const [selectedExternalService, setSelectedExternalService] = useState(null);

  const calendarUrl = listingId
    ? `${apiBaseUrl()}/api/listings/calendar/${listingId.uuid}.ics`
    : null;

  const handleCopy = useCallback(async () => {
    if (!calendarUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(calendarUrl);
      setIsCopied(true);

      setTimeout(() => {
        setIsCopied(false);
      }, 1000);

      console.log('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }, [calendarUrl]);

  const onOpenModal = ({ e, service = null }) => {
    e.preventDefault();

    if (service) {
      setSelectedExternalService(service);
    } else {
      setSelectedExternalService({ title: '', icalUrl: '' });
    }

    setIsExternalServiceModalOpen(true);
  };

  const onCloseModal = () => {
    setIsExternalServiceModalOpen(false);
    setSelectedExternalService(null);
  };

  const onSaveExternalService = service => {
    if (service.id || service.tempId) {
      onUpdateExternalService({ service });
    } else {
      const newService = { ...service, tempId: uuidv4(), listingId: listingId.uuid };

      onAddExternalService({ service: newService });
    }

    onCloseModal();
  };

  const handleRemoveExternalService = ({ e, service }) => {
    e.preventDefault();

    onRemoveExternalService({
      id: service.id || service.tempId,
      isSavedService: service.id ? true : false,
    });
  };

  return (
    <div className={css.wrapper}>
      <div className={css.titleWrapper}>
        <p className={css.title}>
          <FormattedMessage id="AvailabilityIntegration.title" />
        </p>
        <p className={css.description}>
          <FormattedMessage id="AvailabilityIntegration.description" />
        </p>
        <p className={css.description}>
          <FormattedMessage id="AvailabilityIntegration.tip" />
        </p>
      </div>

      {listingId ? (
        <>
          <div className={css.copyLinkWrapper}>
            <input
              type="text"
              value={
                calendarUrl ||
                intl.formatMessage({
                  id: 'AvailabilityIntegration.inputWarning',
                })
              }
              readOnly
            />

            <button
              type="button"
              onClick={handleCopy}
              className={css.button}
              disabled={!calendarUrl || isCopied}
              title={intl.formatMessage({
                id: 'AvailabilityIntegration.copyButtonLabel',
              })}
            >
              {!isCopied ? <IconCopy /> : <IconCheckmark size="small" className={css.checkmark} />}
            </button>
          </div>

          <div className={css.externalServicesWrapper}>
            {externalServices.map(service => (
              <div key={service?.id || service?.tempId} className={css.externalServiceItem}>
                <div className={css.externalServiceItemContent}>
                  <p className={css.externalServiceTitle}>{service.title}</p>
                  <p className={css.externalServiceIcalUrl}>{service.icalUrl}</p>
                </div>
                <div className={css.externalServiceItemActions}>
                  <button
                    type="button"
                    className={css.button}
                    onClick={e => onOpenModal({ e, service })}
                    disabled={!listingId}
                  >
                    <IconEdit className={css.pencilIcon} />
                  </button>
                  <button
                    type="button"
                    className={css.button}
                    onClick={e => handleRemoveExternalService({ e, service })}
                    disabled={!listingId}
                  >
                    <IconDelete />
                  </button>
                </div>
              </div>
            ))}
            {externalServices.length < 5 ? (
              <InlineTextButton
                className={css.addExternalServiceButton}
                onClick={e => onOpenModal({ e })}
                disabled={!listingId}
              >
                <FormattedMessage id="AvailabilityIntegration.addExternalService" />
              </InlineTextButton>
            ) : null}
          </div>

          <Modal
            id="externalServiceModal"
            isOpen={isExternalServiceModalOpen}
            onClose={() => onCloseModal()}
            usePortal
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.modalWrapper}>
              <div className={css.modalInput}>
                <label htmlFor="externalServiceTitle">
                  <FormattedMessage id="AvailabilityIntegration.externalServiceTitle" />
                </label>
                <input
                  id="externalServiceTitle"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'AvailabilityIntegration.externalServiceTitlePlaceholder',
                  })}
                  value={selectedExternalService?.title || ''}
                  onChange={e =>
                    setSelectedExternalService({
                      ...selectedExternalService,
                      title: e.target.value,
                    })
                  }
                  maxLength={EXTERNAL_SERVICE_TITLE_MAX_LENGTH}
                />
              </div>
              <div className={css.modalInput}>
                <label htmlFor="externalServiceIcalUrl">
                  <FormattedMessage id="AvailabilityIntegration.externalServiceIcalUrl" />
                </label>
                <input
                  id="externalServiceIcalUrl"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'AvailabilityIntegration.externalServiceIcalUrlPlaceholder',
                  })}
                  value={selectedExternalService?.icalUrl || ''}
                  onChange={e =>
                    setSelectedExternalService({
                      ...selectedExternalService,
                      icalUrl: e.target.value,
                    })
                  }
                />
              </div>
              <div className={css.modalButton}>
                <Button
                  onClick={() => onSaveExternalService(selectedExternalService)}
                  disabled={!selectedExternalService?.title || !selectedExternalService?.icalUrl}
                >
                  <FormattedMessage id="AvailabilityIntegration.saveButton" />
                </Button>
              </div>
            </div>
          </Modal>
        </>
      ) : (
        <p className={css.newListingMessage}>
          <FormattedMessage id="AvailabilityIntegration.newListingMessage" />
        </p>
      )}
    </div>
  );
};

export default AvailabilityIntegration;
